import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { notifyWorker } from '../../lib/notifyWorker'

function fmtDate(s) {
  if (!s) return ''
  return new Date(s + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

// Group consecutive same-reason rows from one user into a single visible item
// so a 7-day vacation request shows as a range, not 7 separate rows.
function groupConsecutive(rows) {
  // Sort by user, reason, date.
  var sorted = rows.slice().sort(function(a, b) {
    if (a.user_id !== b.user_id) return a.user_id < b.user_id ? -1 : 1
    var ra = a.reason || '', rb = b.reason || ''
    if (ra !== rb) return ra < rb ? -1 : 1
    return a.blocked_date < b.blocked_date ? -1 : 1
  })
  var groups = []
  var cur = null
  function nextDate(iso) {
    var d = new Date(iso + 'T12:00:00')
    d.setDate(d.getDate() + 1)
    return d.toISOString().slice(0, 10)
  }
  function nextNonSundayDate(iso) {
    var d = new Date(iso + 'T12:00:00')
    d.setDate(d.getDate() + 1)
    if (d.getDay() === 0) d.setDate(d.getDate() + 1)
    return d.toISOString().slice(0, 10)
  }
  sorted.forEach(function(r) {
    var sameRun = cur
      && cur.user_id === r.user_id
      && (cur.reason || '') === (r.reason || '')
      && (r.blocked_date === nextDate(cur.lastDate) || r.blocked_date === nextNonSundayDate(cur.lastDate))
    if (!sameRun) {
      cur = {
        ids: [r.id],
        user_id: r.user_id,
        worker: r.profiles,
        reason: r.reason || null,
        firstDate: r.blocked_date,
        lastDate: r.blocked_date,
        rows: [r],
      }
      groups.push(cur)
    } else {
      cur.ids.push(r.id)
      cur.lastDate = r.blocked_date
      cur.rows.push(r)
    }
  })
  return groups
}

export default function BlockedDatesApprovalPanel() {
  var { profile, isOwner } = useAuth()
  var canApprove = isOwner || profile?.role === 'office_staff' || profile?.role === 'project_manager'
  var [requests, setRequests] = useState([])
  var [loading, setLoading] = useState(true)
  var [collapsed, setCollapsed] = useState(false)
  var [busyIds, setBusyIds] = useState({})

  var load = useCallback(async function() {
    if (!canApprove) { setLoading(false); return }
    setLoading(true)
    var { data, error } = await supabase.from('worker_blocked_dates')
      .select('*, profiles:user_id(full_name, role, business_ids)')
      .eq('status', 'pending')
      .order('blocked_date', { ascending: true })
    if (error) console.warn('[BlockedDatesApprovalPanel] load failed', error)
    setRequests(data || [])
    setLoading(false)
  }, [canApprove])

  useEffect(function() { load() }, [load])

  var groups = useMemo(function() { return groupConsecutive(requests) }, [requests])

  // Bulk-by-worker: every pending block from that worker.
  var workerCounts = useMemo(function() {
    var c = {}
    requests.forEach(function(r) { c[r.user_id] = (c[r.user_id] || 0) + 1 })
    return c
  }, [requests])

  var setBusy = function(ids, on) {
    setBusyIds(function(prev) {
      var next = Object.assign({}, prev)
      ids.forEach(function(id) { if (on) next[id] = true; else delete next[id] })
      return next
    })
  }

  var approveIds = async function(ids, workerId, summary) {
    if (!canApprove) return
    setBusy(ids, true)
    var { error } = await supabase.from('worker_blocked_dates')
      .update({ status: 'approved', approved_by: profile.id })
      .in('id', ids)
    if (error) {
      alert('Approve failed: ' + error.message)
      setBusy(ids, false)
      return
    }
    if (workerId) {
      await notifyWorker({
        userId: workerId,
        type: 'blocked_date_approved',
        title: 'Time off approved',
        message: summary,
        link: '/me/settings',
      })
    }
    setBusy(ids, false)
    await load()
  }

  var denyIds = async function(ids, workerId, summary) {
    if (!canApprove) return
    if (!window.confirm('Deny this request? The worker will be notified.')) return
    setBusy(ids, true)
    var { error } = await supabase.from('worker_blocked_dates')
      .update({ status: 'denied', approved_by: profile.id })
      .in('id', ids)
    if (error) {
      alert('Deny failed: ' + error.message)
      setBusy(ids, false)
      return
    }
    if (workerId) {
      await notifyWorker({
        userId: workerId,
        type: 'blocked_date_denied',
        title: 'Time off denied',
        message: summary + ' — please find a replacement.',
        link: '/me/settings',
      })
    }
    setBusy(ids, false)
    await load()
  }

  var approveAllForWorker = async function(workerId, workerName) {
    if (!window.confirm('Approve every pending block from ' + (workerName || 'this worker') + '?')) return
    var ids = requests.filter(function(r) { return r.user_id === workerId }).map(function(r) { return r.id })
    if (ids.length === 0) return
    await approveIds(ids, workerId, ids.length + ' day' + (ids.length === 1 ? '' : 's') + ' approved')
  }

  if (!canApprove) return null
  if (loading) return null
  if (requests.length === 0) return null

  return (
    <div style={st.wrap}>
      <div style={st.header} onClick={function() { setCollapsed(!collapsed) }}>
        <div style={st.title}>
          PENDING TIME OFF REQUESTS
          <span style={st.badge}>{requests.length}</span>
        </div>
        <div style={st.toggle}>{collapsed ? '▸' : '▾'}</div>
      </div>
      {!collapsed && (
        <div style={st.list}>
          {groups.map(function(g) {
            var key = g.ids.join('-')
            var label = g.firstDate === g.lastDate ? fmtDate(g.firstDate) : (fmtDate(g.firstDate) + ' → ' + fmtDate(g.lastDate))
            var nDays = g.ids.length
            var summary = label + (nDays > 1 ? ' (' + nDays + ' days)' : '') + (g.reason ? ' — ' + g.reason : '')
            var workerName = g.worker?.full_name || 'Worker'
            var busy = g.ids.some(function(id) { return busyIds[id] })
            var totalForWorker = workerCounts[g.user_id] || 0
            return (
              <div key={key} style={st.row}>
                <div style={st.main}>
                  <div style={st.name}>{workerName}</div>
                  <div style={st.meta}>
                    <span style={st.date}>{label}</span>
                    {nDays > 1 && <span style={st.daysTag}>{nDays} days</span>}
                    {g.reason && <span style={st.reason}>— {g.reason}</span>}
                  </div>
                </div>
                <div style={st.actions}>
                  <button disabled={busy} onClick={function() { approveIds(g.ids, g.user_id, summary) }} style={st.approveBtn}>
                    APPROVE
                  </button>
                  <button disabled={busy} onClick={function() { denyIds(g.ids, g.user_id, summary) }} style={st.denyBtn}>
                    DENY
                  </button>
                  {totalForWorker > nDays && (
                    <button disabled={busy} onClick={function() { approveAllForWorker(g.user_id, workerName) }} style={st.bulkBtn}>
                      APPROVE ALL FROM {workerName.split(' ')[0].toUpperCase()} ({totalForWorker})
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

var st = {
  wrap: { marginBottom: '20px', border: '1px solid rgba(180,140,60,0.3)', borderRadius: '4px', backgroundColor: 'rgba(180,140,60,0.04)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', cursor: 'pointer' },
  title: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '2px', color: '#b48c3c', display: 'flex', alignItems: 'center', gap: '10px' },
  badge: { backgroundColor: '#b48c3c', color: '#0a0f1a', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 700 },
  toggle: { color: 'rgba(255,255,255,0.4)', fontSize: '14px' },

  list: { display: 'flex', flexDirection: 'column', gap: '6px', padding: '0 12px 12px' },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '3px', flexWrap: 'wrap' },

  main: { flex: 1, minWidth: '200px' },
  name: { fontSize: '14px', color: '#ffffff', fontWeight: 500 },
  meta: { fontSize: '13px', color: 'rgba(255,255,255,0.55)', marginTop: '3px', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' },
  date: { color: '#b48c3c', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '1px', fontWeight: 600 },
  daysTag: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', letterSpacing: '1px', padding: '2px 7px', borderRadius: '10px', backgroundColor: 'rgba(180,140,60,0.1)', border: '1px solid rgba(180,140,60,0.3)', color: '#b48c3c' },
  reason: { fontStyle: 'italic' },

  actions: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  approveBtn: { backgroundColor: '#4a8c6a', border: 'none', borderRadius: '3px', padding: '8px 12px', color: '#0a0f1a', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer' },
  denyBtn: { backgroundColor: 'transparent', border: '1px solid rgba(224,92,92,0.3)', borderRadius: '3px', padding: '8px 12px', color: '#e05c5c', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', letterSpacing: '1.5px', cursor: 'pointer' },
  bulkBtn: { backgroundColor: 'transparent', border: '1px solid rgba(180,140,60,0.3)', borderRadius: '3px', padding: '8px 12px', color: '#b48c3c', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', fontWeight: 600, letterSpacing: '1.5px', cursor: 'pointer' },
}
